import { Box, Grid, GridItem, Stack } from '@chakra-ui/react';
import gql from 'graphql-tag';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayProperty } from '~components/displays/DisplayDetail/connectivity/ConnectivitySection';
import { getPlatformLabel } from '~components/displays/utils';
import { DisplayInfoSection_DisplayFragment } from './__generated__/DisplayInfoSection.graphql';
import { DisplayFirmwareUpdate } from './DisplayFirmwareUpdate';

interface Props {
  display: DisplayInfoSection_DisplayFragment;
}

export function DisplayInfoSection({ display }: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'guest' | 'host'>('guest');

  // Check if host data exists
  const hasHost = display.host !== null && display.host !== undefined;

  // Get platform labels
  const guestPlatformLabel = display.platform?.name || 'GUEST';
  const hostPlatformLabel = display.host?.platformLabel || 'HOST';

  return (
    <>
      <GridItem colSpan={{ base: 8, md: hasHost ? 8 : 7 }} mb={hasHost ? 0 : 10}>
        {/* Show tabs only if host exists */}
        {hasHost && (
          <Box borderBottom="1px solid" borderColor="gray.200" mb="6">
            <Stack direction="row" spacing="0">
              <Box
                as="button"
                px="4"
                py="2"
                borderBottom="2px solid"
                borderColor={activeTab === 'guest' ? 'blue.500' : 'transparent'}
                color={activeTab === 'guest' ? 'blue.500' : 'gray.600'}
                fontWeight={activeTab === 'guest' ? 'semibold' : 'normal'}
                _hover={{ color: 'blue.500' }}
                onClick={() => setActiveTab('guest')}
              >
                {guestPlatformLabel}
              </Box>
              <Box
                as="button"
                px="4"
                py="2"
                borderBottom="2px solid"
                borderColor={activeTab === 'host' ? 'blue.500' : 'transparent'}
                color={activeTab === 'host' ? 'blue.500' : 'gray.600'}
                fontWeight={activeTab === 'host' ? 'semibold' : 'normal'}
                _hover={{ color: 'blue.500' }}
                onClick={() => setActiveTab('host')}
              >
                {hostPlatformLabel}
              </Box>
            </Stack>
          </Box>
        )}

        {/* GUEST Tab Content - shows when no host or when guest tab is active */}
        {(!hasHost || activeTab === 'guest') && (
          <>
            <Box mb={hasHost ? '6' : '10'}>
              <DisplayFirmwareUpdate display={display} />
            </Box>
            <Grid templateColumns="repeat(2, 1fr)" gap="0 10">
              <GridItem>
                <Stack direction="column" spacing="5">
                  <DisplayProperty
                    label={t('firmware')}
                    value={
                      display.firmware?.scaler
                        ? display.firmware?.android.version +
                          ' + ' +
                          display.firmware?.scaler.version
                        : display.firmware?.android.version
                    }
                  />
                  <DisplayProperty
                    label={t('platform')}
                    value={getPlatformLabel(display.platform)}
                  />
                  <DisplayProperty label={t('serialNumber')} value={display.serialNumber} />
                </Stack>
              </GridItem>
              <GridItem>
                <Stack direction="column" spacing="5">
                  <DisplayProperty label={t('waveApp')} value={display.agentVersion || 'Unknown'} />
                  <DisplayProperty label={t('modelNumber')} value={display.commercialTypeNumber} />
                </Stack>
              </GridItem>
            </Grid>
          </>
        )}

        {/* HOST Tab Content */}
        {hasHost && activeTab === 'host' && (
          <Grid templateColumns="repeat(2, 1fr)" gap="0 10">
            <GridItem>
              <Stack direction="column" spacing="5">
                <DisplayProperty label={t('firmware')} value={display.host?.firmwareVersion} />
                <DisplayProperty label={t('platform')} value={display.host?.platformLabel} />
                <DisplayProperty label={t('serialNumber')} value={display.host?.serialNumber} />
              </Stack>
            </GridItem>
            <GridItem>
              <Stack direction="column" spacing="5">
                <DisplayProperty label={t('modelNumber')} value={display.host?.modelName} />
              </Stack>
            </GridItem>
          </Grid>
        )}
      </GridItem>
    </>
  );
}

DisplayInfoSection.graphql = {
  fragments: {
    DisplayInfoSection_display: gql`
      fragment DisplayInfoSection_display on Display {
        id
        commercialTypeNumber
        serialNumber
        platform {
          name
          version
          type
        }
        firmware {
          android {
            version
          }
          scaler {
            version
          }
        }
        agentVersion
        host {
          platformLabel
          firmwareVersion
          serialNumber
          modelName
        }
        ...DisplayFirmwareUpdate_display
      }
    `,
  },
};
